<template>
  <ResponsiveTableAdv
    :columns="columns"
    :init-show-cols="initShowCols"
    :data="correctedData"
    :more="false"
    :loading="loading"
    :csv-file-name="csvFileName"
    :top-controls="false"
    :show-col-picker="showColPicker"
    class="projects-data-table"
    @row-click="$emit('goto-project', $event)"
  >
    <template #prepend>
      <a v-tooltip="'View project'" class="btn btn-link" aria-label="View project"
        ><i class="glyphicon glyphicon glyphicon-chevron-right"
      /></a>
    </template>
    <template #favorite="{ row: { accessLevel, favorite, subscribed, subName, id, systemTags } }">
      <a
        v-tooltip="favorite ? 'Favorite project' : 'Click to add to favorites'"
        aria-label="favorite ? 'Favorite project' : 'Click to add to favorites'"
        :class="{ disabled: accessLevel === 'NONE' }"
        :disabled="accessLevel === 'NONE'"
        @click.stop="toggleFavorite(favorite, id)"
        @keyup.enter="toggleFavorite(favorite, id)"
      >
        <i v-if="favorite" class="glyphicon glyphicon-star" />
        <i v-else class="glyphicon glyphicon-star-empty" />
      </a>
      {{ ' ' }}
      <a
        v-tooltip="clipboardTooltipText(id)"
        aria-label="Copy the URL for this project to your clipboard"
        :class="{
          disabled: accessLevel === 'NONE',
          disableLink: clipboardTooltipText(id) === 'Copied to clipboard',
        }"
        @click.stop="copyProjectUrlToClipboard(id)"
        @keyup.enter="copyProjectUrlToClipboard(id)"
      >
        <i class="glyphicon glyphicon-link" />
      </a>
      {{ ' ' }}
      <UserPlusIcon v-if="accessLevel === 'OWNER'" v-tooltip="'Project owner'" size="16" />
      <UserCheckIcon v-else-if="accessLevel === 'EDITOR'" v-tooltip="'Project editor'" size="16" />
      <UserIcon v-else-if="accessLevel === 'ANALYST'" v-tooltip="'Project analyst'" size="16" />
      <UserMinusIcon v-else-if="accessLevel === 'VIEWER'" v-tooltip="'Project viewer'" size="16" />
      <UserXIcon v-else-if="accessLevel === 'NONE'" v-tooltip="'No view access'" size="16" />
      {{ ' ' }}
      <a
        v-if="accessLevel === 'EDITOR' || accessLevel === 'OWNER'"
        v-tooltip="iconTip('Add new API Call')"
        aria-label="Add new API call"
        @click.stop="addAPICall()"
        @keyup.enter="addAPICall()"
      >
        <i class="glyphicon glyphicon-plus" />
      </a>
      {{ ' ' }}
      <a
        v-if="!subName"
        v-tooltip="
          iconTip(
            subscribed
              ? 'Subscribed to project update emails'
              : 'Click to subscribe to project update emails',
          )
        "
        :aria-label="
          subscribed
            ? 'Subscribed to project update emails'
            : 'Click to subscribe to project update emails'
        "
        :class="{ 'text-accent': subscribed, 'text-white': !subscribed }"
        @click.stop="toggleSubscription(subscribed, id)"
        @keyup.enter="toggleSubscription(subscribed, id)"
      >
        <i class="glyphicon glyphicon-envelope" />
      </a>
      {{ ' ' }}
      <span @click.stop>
        <router-link v-if="standalone" :to="{ name: 'billing-info', params: { subId: id } }">
          <SubscriptionPill :system-tags="systemTags" />
        </router-link>
      </span>
    </template>
    <template #projectName="{ row }">
      <a class="larger">
        {{ row.name }}
      </a>
      <small v-if="data.subName"> ({{ row.subName }}) </small>
    </template>
    <template #recentResults="{ row: { results = [] } }">
      <div class="results-row pull-right">
        <span
          v-for="(n, idx) in 10"
          :key="'recent-' + idx"
          v-tooltip="resultsTooltip(results.slice(idx * 10, idx * 10 + 10))"
          :class="resultsClasses(results.slice(idx * 10, idx * 10 + 10))"
          :style="results.length ? resultStyle(results.slice(idx * 10, idx * 10 + 10)[0]) : {}"
        />
      </div>
    </template>
    <template #meta="{ row }">
      <span
        v-if="allPasses(row)"
        v-tooltip="iconTip('100% pass rate during the week.')"
        class="color-pass"
      >
        <CheckCircleIcon />
      </span>
      {{ ' ' }}

      <span
        v-if="anyFails(row)"
        v-tooltip="iconTip('There were failures during the week.')"
        class="color-fail"
      >
        <XCircleIcon />
      </span>
    </template>
  </ResponsiveTableAdv>
</template>

<script>
import { call } from 'vuex-pathify';
import moment from 'moment-timezone';
import _groupBy from 'lodash/groupBy';
import {
  CheckCircleIcon,
  XCircleIcon,
  // AlertTriangleIcon,
  UserPlusIcon,
  UserCheckIcon,
  UserIcon,
  UserMinusIcon,
  UserXIcon,
  // LockIcon,
} from 'vue-feather-icons';
import ResponsiveTableAdv from '../ResponsiveTableAdv.vue';
import SubscriptionPill from '../SubscriptionPill.vue';
import newCallPayload from '../../js/new_call';

export default {
  name: 'ProjectsDataTable2',
  components: {
    ResponsiveTableAdv,
    CheckCircleIcon,
    // AlertTriangleIcon,
    XCircleIcon,
    UserPlusIcon,
    UserCheckIcon,
    UserIcon,
    UserMinusIcon,
    UserXIcon,
    SubscriptionPill,
  },
  props: {
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    data: {
      type: Array,
      required: true,
    },
    showColPicker: {
      type: Boolean,
      required: false,
      default: true,
    },
    standalone: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    const filterable = true;
    const numeric = true;
    const right = true;
    const sortable = true;
    const noColPick = true;

    return {
      lastCopiedToClipboard: undefined,
      initShowCols: [
        '_',
        'favorite',
        'name',
        'results',
        'synopsis.insights.score',
        'synopsis.resultPcts.availability',
        'synopsis.resultPcts.SLOW',
        'synopsis.avgTimings.total',
        'meta',
      ],
      columns: [
        {
          id: '_',
          title: ' ',
          type: 'prepend',
          sortable: false,
          thClass: { 'th-narrow': true },
          noColPick,
          hideTitle: true,
          sortFn: ({ favorite }) => (favorite ? 1 : 0),
          textFn: (row) =>
            `${process.env.VUE_APP_URL}${
              this.$router.resolve({
                name: 'projects-home',
                query: {
                  project_key_str: row.id,
                },
              }).href
            }`,
        },
        {
          id: 'favorite',
          title: '*',
          type: 'favorite',
          sortable,
          thClass: { 'th-narrow': true },
          noColPick,
          textFn: ({ favorite }) => favorite,
          sortFn: ({ favorite }) => (favorite ? 1 : 0),
        },
        {
          id: 'name',
          title: 'Name',
          type: 'projectName',
          sortable,
          // filterable: true,
          textFn: (data) => data.name,
        },
        {
          id: 'results',
          title: 'Recent results',
          type: 'recentResults',
          right,
          textFn: () => '',
          tooltip: 'Breakdown of the last 100 API calls made',
          sortFn: ({ results }) => {
            if (results && results.length) {
              const { pass = 0, slow = 0, warning = 0, failure = 0 } = this.resultStats(results);
              const sum = failure + warning + slow + pass;
              if (sum > 0) {
                // Return these as strings so sort function
                // happy and pad them so it doesn't go 1, 10, 2...
                return [
                  (failure / sum).toFixed(5),
                  (warning / sum).toFixed(5),
                  (slow / sum).toFixed(5),
                  (-pass).toString(10).padStart(8, '0'),
                ];
              }
            }
            return null;
          },
        },
        {
          id: 'synopsis.insights.score',
          title: 'Quality',
          type: 'casc',
          sortable,
          filterable,
          numeric,
          right,
          tdClass: { larger: true },
          tooltip: 'Cloud API Service Consistency is calculated once per week every Monday',
        },
        {
          id: 'synopsis.resultPcts.availability',
          title: 'Availability %',
          filterable,
          numeric,
          type: 'percentage',
          right,
          tooltip: 'Percentage of API calls not resulting in a Fail state',
        },
        {
          id: 'synopsis.resultPcts.PASS',
          title: 'Pass %',
          filterable,
          numeric,
          type: 'percentage',
          right,
          tooltip: 'Percentage of API calls resulting in a Pass state',
        },
        {
          id: 'synopsis.resultPcts.SLOW',
          title: 'Slow %',
          filterable,
          numeric,
          type: 'percentage',
          right,
          tooltip: 'Percentage of API calls resulting in a Slow state',
        },
        {
          id: 'synopsis.resultPcts.WARNING',
          title: 'Warning %',
          filterable,
          numeric,
          type: 'percentage',
          right,
          tooltip: 'Percentage of API calls resulting in a Warning state',
        },
        {
          id: 'synopsis.resultPcts.FAIL',
          title: 'Fail %',
          filterable,
          numeric,
          type: 'percentage',
          right,
          tooltip: 'Percentage of API calls resulting in a Fail state',
        },
        {
          id: 'synopsis.avgTimings.dns',
          title: 'Avg. DNS lookup',
          filterable,
          numeric,
          type: 'ms',
          right,
          tooltip: 'Mean time to look up the domain name',
        },
        {
          id: 'synopsis.avgTimings.tcp',
          title: 'Avg. TCP connection',
          filterable,
          numeric,
          type: 'ms',
          right,
          tooltip: 'Mean time for a TCP connection to be made between our agent and the endpoint',
        },
        {
          id: 'synopsis.avgTimings.handshake',
          title: 'Avg. SSL Handshake',
          filterable,
          numeric,
          type: 'ms',
          right,
          tooltip: 'Mean time for the SSL/TLS handshake to be completed',
        },
        {
          id: 'synopsis.avgTimings.network',
          title: 'Avg. Network',
          filterable,
          numeric,
          type: 'ms',
          right,
          tooltip: 'Mean time for the TCP connection and SSL handshake to be completed',
        },
        {
          id: 'synopsis.avgTimings.upload',
          title: 'Avg. Upload',
          filterable,
          numeric,
          type: 'ms',
          right,
          tooltip: 'Mean time between for our agent to send the request',
        },
        {
          id: 'synopsis.avgTimings.process',
          title: 'Avg. Processing',
          filterable,
          numeric,
          type: 'ms',
          right,
          tooltip:
            'Mean time between our agent sending the request and getting the first byte of the response',
        },
        {
          id: 'synopsis.avgTimings.download',
          title: 'Avg. Download',
          filterable,
          numeric,
          type: 'ms',
          right,
          tooltip: 'Mean time between receiving the first and last bytes of the response',
        },
        {
          id: 'synopsis.avgTimings.server',
          title: 'Avg. Server',
          filterable,
          numeric,
          type: 'ms',
          right,
          tooltip: 'Mean time to send request and receive the full response',
        },
        {
          id: 'synopsis.avgTimings.total_no_dns',
          title: 'Avg. Total latency excluding DNS',
          filterable,
          numeric,
          type: 'ms',
          right,
          tooltip: 'Mean time to complete the request excluding DNS',
        },
        {
          id: 'synopsis.avgTimings.total',
          title: 'Avg. Total latency',
          filterable,
          numeric,
          type: 'ms',
          right,
          tooltip: 'Mean time to complete the request',
        },
        {
          id: 'meta',
          title: 'Additional info',
          type: 'meta',
          sortable: false,
          hideTitle: true,
        },
      ],
    };
  },
  computed: {
    csvFileName() {
      return 'ProjectsData.csv';
    },
    correctedData() {
      return this.data.map((item) => {
        const { synopsis } = item;
        if (synopsis) {
          if (synopsis.timings && synopsis.timings.count > 0) {
            synopsis.avgTimings = {
              dns: synopsis.timings.dns / (synopsis.timings.count * 1000),
              tcp: synopsis.timings.tcp / (synopsis.timings.count * 1000),
              handshake: synopsis.timings.handshake / (synopsis.timings.count * 1000),
              network:
                (synopsis.timings.dns + synopsis.timings.tcp + synopsis.timings.handshake) /
                (synopsis.timings.count * 1000),
              upload: synopsis.timings.upload / (synopsis.timings.count * 1000),
              process: synopsis.timings.process / (synopsis.timings.count * 1000),
              download: synopsis.timings.download / (synopsis.timings.count * 1000),
              server:
                (synopsis.timings.upload + synopsis.timings.process + synopsis.timings.download) /
                (synopsis.timings.count * 1000),
              total_no_dns:
                (synopsis.timings.total - synopsis.timings.dns) / (synopsis.timings.count * 1000),
              total: synopsis.timings.total / (synopsis.timings.count * 1000),
              count: synopsis.timings.count,
            };
          }
          if (synopsis.result_counts) {
            synopsis.resultPcts = {
              availability:
                (synopsis.result_counts.total - synopsis.result_counts.FAIL) /
                synopsis.result_counts.total,
              PASS: synopsis.result_counts.PASS / synopsis.result_counts.total,
              SLOW: synopsis.result_counts.SLOW / synopsis.result_counts.total,
              WARNING: synopsis.result_counts.WARNING / synopsis.result_counts.total,
              FAIL: synopsis.result_counts.FAIL / synopsis.result_counts.total,
              total: synopsis.result_counts.total,
            };
          }
          if (synopsis.insights) {
            synopsis.insights.scoreRoute = {
              name: 'insights',
              query: { project_key_str: item.projectId },
            };
          }
        }
        return {
          ...item,
          id: item.projectId,
        };
      });
    },
  },
  methods: {
    groupedResults(results) {
      const aDayAgo = moment.utc().add(-1, 'days');
      const filtered = results.filter(
        (res) => res && res.meta && moment.utc(res.meta.start_time) > aDayAgo,
      );
      const groups = _groupBy(filtered, 'meta.result_category');
      return groups;
    },
    resultStats(results) {
      const partitions = this.groupedResults(results);
      const pass = (partitions.PASS || []).length;
      const warning = (partitions.WARNING || []).length;
      const failure = (partitions.FAIL || []).length;
      return {
        pass,
        warning,
        failure,
      };
    },
    resultsClasses(results) {
      const { pass, warning, failure } = this.resultStats(results);
      return {
        'result-box': true,
        'status-pass': pass && !warning && !failure,
        'status-warn': !pass && warning && !failure,
        'status-fail': !pass && !warning && failure,
        'status-warn-pass': pass && warning && !failure,
        'status-fail-warn': !pass && warning && failure,
        'status-fail-pass': pass && !warning && failure,
        'status-fail-warn-pass': pass && warning && failure,
        'status-nodata': !pass && !warning && !failure,
      };
    },
    resultsTooltip(results) {
      const { pass, warning, failure } = this.resultStats(results);
      let descs = 'There have been ';
      if (failure > 0) {
        if (warning > 0) {
          if (pass > 0) {
            descs += 'failures, warnings and passes';
          } else {
            descs += 'failures and warnings';
          }
        } else if (pass > 0) {
          descs += 'failures and passes';
        } else {
          descs += 'only failures';
        }
      } else if (warning > 0) {
        if (pass > 0) {
          descs += 'warnings and passes';
        } else {
          descs += 'only warnings';
        }
      } else if (pass > 0) {
        descs += 'only passes';
      } else {
        return {
          content: 'No results found for last 24 hours',
          boundariesElement: document.body,
        };
      }
      if (results[0]?.meta?.start_time) {
        descs += ` between ${this.humanDate(results[0].meta.start_time)}`;
        descs += ` and ${this.humanDate(results[results.length - 1].meta.start_time)}.`;
      } else {
        descs += ' in recent results.';
      }
      return {
        content: descs,
        boundariesElement: document.body,
      };
    },
    humanDate(date) {
      return moment.utc(date).tz(moment.tz.guess()).format('lll');
    },
    resultStyle(result) {
      if (!result || !result.meta || !result.meta.start_time) {
        return { opacity: 0 };
      }
      const diffInMs = moment.utc().diff(moment.utc(result.meta.start_time));
      const msInADay = 1000 * 60 * 60 * 24;
      const pctThruDay = (msInADay - diffInMs) / msInADay;
      const normalizedOpacity = Math.min(1.0, Math.max(pctThruDay, 0.0));
      const opacity = 0.25 + 0.75 * normalizedOpacity;
      return { opacity };
    },
    iconTip(content) {
      return {
        content,
        boundariesElement: document.body,
      };
    },
    allPasses(data) {
      return (
        data.synopsis?.result_counts?.PASS === data.synopsis?.result_counts?.total &&
        (data.synopsis?.result_counts?.total || 0) > 0
      );
    },
    anyFails(data) {
      return data.synopsis?.result_counts?.FAIL > 0;
    },
    toggleFavorite(favorite, id) {
      this.$emit(favorite ? 'unfav-project' : 'fav-project', id);
    },
    toggleSubscription(subscribed, id) {
      this.$emit(subscribed ? 'unsub-project' : 'sub-project', id);
    },
    projectUrl(id) {
      return `${process.env.VUE_APP_URL}${
        this.$router.resolve({
          name: 'project-home',
          query: {
            project_key_str: id,
          },
        }).href
      }`;
    },
    copyProjectUrlToClipboard(id) {
      this.lastCopiedToClipboard = this.projectUrl(id);
      navigator.clipboard.writeText(this.lastCopiedToClipboard);
      setTimeout(() => {
        this.lastCopiedToClipboard = undefined;
      }, 3000);
    },
    clipboardTooltipText(id) {
      return this.lastCopiedToClipboard === this.projectUrl(id)
        ? 'Copied to clipboard'
        : 'Copy URL for this project to your clipboard';
    },
    async addAPICall() {
      await this.setProject({ id: this.projectId });
      const { id: callId } = await this.createCall(newCallPayload());
      this.$router.push({ name: 'calls-edit', params: { callId } });
    },
    createCall: call('apimAPI/createCall'),
    setProject: call('account2/setProject'),
  },
};
</script>

<style>
.projects-data-table table > tbody > tr > td {
  cursor: pointer;
  vertical-align: middle;
}
</style>

<style scoped>
.results-row {
  text-overflow: clip;
  white-space: nowrap;
  overflow: hidden;
  display: flex;
  flex-direction: row-reverse;
}
.result-box {
  display: inline-block;
  width: 12px;
  height: 24px;
  margin-right: 1px;
  background-color: var(--dark-secondary);
}
/* !important set for printing */
.status-nodata {
  background-color: #999999 !important;
  opacity: 0.25;
}
.status-pass {
  background-color: var(--pass) !important;
}
.status-warn {
  background-color: var(--warn) !important;
}
.status-fail {
  background-color: var(--fail) !important;
}

.status-warn-pass {
  background-color: var(--warn) !important;
  background: linear-gradient(
    135deg,
    var(--warn) 0%,
    var(--warn) 50%,
    var(--pass) 51%,
    var(--pass) 100%
  ) !important;
}
.status-fail-warn {
  background-color: var(--fail);
  background: linear-gradient(
    135deg,
    var(--fail) 0%,
    var(--fail) 50%,
    var(--warn) 51%,
    var(--warn) 100%
  ) !important;
}
.status-fail-pass {
  background-color: var(--fail);
  background: linear-gradient(
    135deg,
    var(--fail) 0%,
    var(--fail) 50%,
    var(--pass) 51%,
    var(--pass) 100%
  ) !important;
}
.status-fail-warn-pass {
  background-color: var(--fail);
  background: linear-gradient(
    135deg,
    var(--fail) 0%,
    var(--fail) 50%,
    var(--warn) 51%,
    var(--warn) 75%,
    var(--pass) 76%,
    var(--pass) 100%
  ) !important;
}
.disableLink {
  cursor: not-allowed !important;
  color: var(--grey) !important;
}
</style>
